import { CellStatus, type SmartCell } from "@/queries/smart-tables/types";

export const DEFAULT_CELL_ERROR_MESSAGE = "Cell computation failed";
const NETWORK_CELL_ERROR_TITLE = "Network Error";
const NETWORK_CELL_ERROR_MESSAGE = "A network error occurred. Please try again.";

const NETWORK_CELL_ERROR_CODES = new Set(["ERR", "ERR_NETWORK"]);
const SERIALIZED_RECORD_SCAN_LIMIT = 8_192;

const getFirstNonWhitespaceChar = (value: string): string | undefined => {
  const scanLength = Math.min(value.length, SERIALIZED_RECORD_SCAN_LIMIT);
  for (let index = 0; index < scanLength; index += 1) {
    const char = value[index];
    if (char && !/\s/.test(char)) {
      return char;
    }
  }

  return undefined;
};

const hasOversizedStringValue = (
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
): boolean => {
  if (typeof value === "string") {
    return value.length > SERIALIZED_RECORD_SCAN_LIMIT;
  }

  if (value === null || typeof value !== "object") {
    return false;
  }

  if (seen.has(value)) {
    return false;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.some(item => hasOversizedStringValue(item, seen));
  }

  return Object.values(value as Record<string, unknown>).some(item =>
    hasOversizedStringValue(item, seen),
  );
};

interface CellLike {
  status?: CellStatus | string | null;
  display_value?: unknown;
  value?: unknown;
}

interface CellDisplayState {
  valueToDisplay: unknown;
  errorMessage?: string;
  hasErrorValue: boolean;
  isErrorState: boolean;
  isMediaValue: boolean;
}

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const parseSerializedRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value !== "string" || value.length > SERIALIZED_RECORD_SCAN_LIMIT) {
    return undefined;
  }

  const firstChar = getFirstNonWhitespaceChar(value);
  if (firstChar !== "{" && firstChar !== "[") {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const asRecordLike = (value: unknown): Record<string, unknown> | undefined =>
  isRecord(value) ? value : parseSerializedRecord(value);

const getRecordMessage = (record: Record<string, unknown>): string | undefined => {
  const { message } = record;
  return typeof message === "string" && message.length > 0 ? message : undefined;
};

const extractErrorMessage = (
  value: unknown,
  seen: Set<unknown> = new Set(),
  knownRecord?: Record<string, unknown>,
): string | undefined => {
  if (value === null || value === undefined || seen.has(value)) {
    return undefined;
  }

  seen.add(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      const message = extractErrorMessage(item, seen);
      if (message) {
        return message;
      }
    }
    return undefined;
  }

  const record = knownRecord ?? asRecordLike(value);
  if (!record) {
    return undefined;
  }

  if ("error" in record) {
    const { error: errorField } = record;

    if (typeof errorField === "string" && errorField.length > 0) {
      const parsedErrorMessage = extractErrorMessage(errorField, seen);
      return parsedErrorMessage ?? errorField;
    }

    const errorRecord = asRecordLike(errorField);
    if (errorRecord) {
      const message = getRecordMessage(errorRecord);
      if (message) {
        return message;
      }

      const nestedErrorMessage = extractErrorMessage(errorRecord, seen);
      if (nestedErrorMessage) {
        return nestedErrorMessage;
      }
    }
  }

  const { message } = record;
  if (typeof message === "string" && message.length > 0) {
    return message;
  }

  if ("value" in record) {
    const { value: nestedValue } = record;
    return extractErrorMessage(nestedValue, seen);
  }

  for (const nestedValue of Object.values(record)) {
    const nestedErrorMessage = extractErrorMessage(nestedValue, seen);
    if (nestedErrorMessage) {
      return nestedErrorMessage;
    }
  }

  return undefined;
};

const hasExplicitErrorField = (errorField: unknown, seen: Set<unknown>): boolean => {
  if (errorField === null || errorField === undefined || errorField === false) {
    return false;
  }

  if (typeof errorField === "string") {
    return errorField.slice(0, SERIALIZED_RECORD_SCAN_LIMIT).trim().length > 0;
  }

  if (typeof errorField === "boolean") {
    return errorField;
  }

  if (typeof errorField === "number") {
    return Number.isFinite(errorField);
  }

  if (Array.isArray(errorField)) {
    return errorField.some(item => hasExplicitErrorField(item, seen));
  }

  const record = asRecordLike(errorField);
  if (!record) {
    return false;
  }

  const { message } = record;
  if (
    typeof message === "string" &&
    message.slice(0, SERIALIZED_RECORD_SCAN_LIMIT).trim().length > 0
  ) {
    return true;
  }

  if ("error" in record) {
    const { error } = record;
    return hasExplicitErrorField(error, seen);
  }

  return false;
};

export const hasErrorPayload = (
  value: unknown,
  seen: Set<unknown> = new Set(),
  knownRecord?: Record<string, unknown>,
): boolean => {
  if (value === null || value === undefined || seen.has(value)) {
    return false;
  }

  seen.add(value);

  if (Array.isArray(value)) {
    return value.some(item => hasErrorPayload(item, seen));
  }

  const record = knownRecord ?? asRecordLike(value);
  if (!record) {
    return false;
  }

  if ("error" in record) {
    const { error } = record;
    return hasExplicitErrorField(error, seen);
  }

  // Only recurse through the known cell envelope shape. Avoid scanning every
  // nested object, because valid JSON/model outputs often contain an `error`
  // property that is not a Table computation failure.
  if ("value" in record) {
    const { value: nestedValue } = record;
    return hasErrorPayload(nestedValue, seen);
  }

  return false;
};

const isFailedStatus = (status: CellLike["status"]): boolean => status === CellStatus.FAILED;

const shouldRenderErrorState = ({
  status,
  errorMessage,
  hasKnownErrorPayload,
}: {
  status?: CellLike["status"];
  errorMessage?: string;
  hasKnownErrorPayload: boolean;
}): boolean => isFailedStatus(status) || Boolean(errorMessage) || hasKnownErrorPayload;

const getCellErrorMessage = (
  value: unknown,
  fallback: string = DEFAULT_CELL_ERROR_MESSAGE,
  hasKnownErrorPayload: boolean = hasErrorPayload(value),
  knownRecord?: Record<string, unknown>,
): string | undefined => {
  if (!hasKnownErrorPayload) {
    return undefined;
  }

  return extractErrorMessage(value, new Set(), knownRecord) ?? fallback;
};

const isNetworkCellError = (message: unknown): boolean =>
  typeof message === "string" && NETWORK_CELL_ERROR_CODES.has(message);

export const getUserFacingCellError = (
  message: string | undefined,
): {
  title: string;
  message: string;
  isNetworkError: boolean;
} => {
  if (isNetworkCellError(message)) {
    return {
      title: NETWORK_CELL_ERROR_TITLE,
      message: NETWORK_CELL_ERROR_MESSAGE,
      isNetworkError: true,
    };
  }

  return {
    title: DEFAULT_CELL_ERROR_MESSAGE,
    message: message ?? DEFAULT_CELL_ERROR_MESSAGE,
    isNetworkError: false,
  };
};

export const isOutputMediaPayload = (value: unknown): value is Record<string, unknown> =>
  isRecord(value) && value.type === "output_media";

export const isOutputMediaPayloadArray = (value: unknown): value is Record<string, unknown>[] =>
  Array.isArray(value) && value.length > 0 && isOutputMediaPayload(value[0]);

const isOutputMediaValue = (value: unknown): boolean =>
  isOutputMediaPayload(value) || isOutputMediaPayloadArray(value);

export const stringifyDisplayValue = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const record = asRecordLike(value);
  const hasErrorValue = hasErrorPayload(value, new Set(), record);
  const errorMessage = hasErrorValue ? extractErrorMessage(value, new Set(), record) : undefined;
  if (errorMessage) {
    return errorMessage;
  }

  if (hasErrorValue) {
    return DEFAULT_CELL_ERROR_MESSAGE;
  }

  if (typeof value === "string") {
    return value;
  }

  if (hasOversizedStringValue(value)) {
    return "[Large structured value]";
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const stringifyPrimitiveOrStructured = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  return stringifyDisplayValue(value) ?? "";
};

const getFallbackCellDisplayValue = (cell?: CellLike | null): string => {
  if (!cell) {
    return "";
  }

  const { display_value: displayValue, value } = cell;

  if (displayValue !== null && displayValue !== undefined) {
    return stringifyPrimitiveOrStructured(displayValue);
  }

  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (isRecord(value) && "value" in value) {
    const { value: nestedValue } = value;
    if (
      typeof nestedValue === "string" ||
      typeof nestedValue === "number" ||
      typeof nestedValue === "boolean"
    ) {
      return String(nestedValue);
    }
    return stringifyDisplayValue(nestedValue) ?? stringifyDisplayValue(value) ?? "";
  }

  return stringifyDisplayValue(value) ?? "";
};

export const resolveCellDisplayState = (
  cell: Pick<SmartCell, "status" | "display_value" | "value"> | CellLike | undefined | null,
): CellDisplayState => {
  const status = cell?.status;
  const rawValue = cell?.value;
  const rawRecord = asRecordLike(rawValue);
  const hasErrorValue = hasErrorPayload(rawValue, new Set(), rawRecord);
  const errorMessage = getCellErrorMessage(
    rawValue,
    DEFAULT_CELL_ERROR_MESSAGE,
    hasErrorValue,
    rawRecord,
  );
  const isErrorState = shouldRenderErrorState({
    status,
    errorMessage,
    hasKnownErrorPayload: hasErrorValue,
  });
  const isMediaValue = isOutputMediaValue(rawValue);

  let valueToDisplay: unknown = cell?.display_value;

  if (isErrorState) {
    valueToDisplay = errorMessage ?? DEFAULT_CELL_ERROR_MESSAGE;
  } else if (isMediaValue) {
    valueToDisplay = rawValue;
  }

  if (valueToDisplay === null || valueToDisplay === undefined) {
    valueToDisplay = getFallbackCellDisplayValue(cell);
  } else if (!isMediaValue && typeof valueToDisplay === "object") {
    valueToDisplay = stringifyPrimitiveOrStructured(valueToDisplay);
  }

  return {
    valueToDisplay,
    errorMessage,
    hasErrorValue,
    isErrorState,
    isMediaValue,
  };
};
